/* Needs-attention line parsing for the My Clients cards (client-first IA
 * increment 1, spec §1).
 *
 * The card shows the portfolio row's `attention` string VERBATIM — it is
 * server-derived (tools/ws_profiles.py::portfolio joins the fragments with
 * " · "), never re-computed here. This module only splits that string back
 * into its fragments and classifies each one for TWO presentation concerns
 * the spec calls for:
 *
 *  1. SEMANTIC COLOR per the red budget (design/specs/pill-archetypes.md §4,
 *     spec §1): overdue/regressed → red · waiting/stalled/stuck/stale → amber ·
 *     awaiting-verification → violet · "on track" → quiet. No fabricated
 *     urgency: a quiet card's only fragment ("on track") is muted.
 *  2. A per-fragment DEEP-LINK filter (`wsf`) into that client's board. The
 *     board understands exactly three `?wsf=` kinds today (state/workspace.ts:
 *     overdue | stalled | assignee:<name>), so those fragments pre-filter; a
 *     fragment with no board filter (`wsf: null`) still deep-links into the
 *     board, just unfiltered. The mapping mirrors portfolio.ts's attParts()
 *     regexes so the two surfaces stay consistent.
 */

export type AttentionTone = 'red' | 'amber' | 'violet' | 'quiet';

export interface AttentionFragment {
  /** The fragment text, verbatim from the server's attention string. */
  text: string;
  /** Semantic color per the red budget. */
  tone: AttentionTone;
  /** Board `?wsf=` filter for the deep-link, or null when the board has no
   *  matching pre-filter (the fragment still links into the board). */
  wsf: string | null;
}

/** Classify one server-emitted attention fragment. Pure; unit-tested. The
 *  regexes mirror ws_profiles.py::portfolio's fragment wording and
 *  portfolio.ts::attParts's deep-link patterns. */
export function classifyFragment(text: string): AttentionFragment {
  const t = text.trim();
  // red — the sharpest adverse states (the "one red" budget)
  if (/\boverdue\b/.test(t)) return { text: t, tone: 'red', wsf: 'overdue' };
  if (/\bregressed\b/.test(t)) return { text: t, tone: 'red', wsf: null };
  // violet — awaiting-verification (distinct from the amber waiting states)
  if (/awaiting verification/.test(t)) return { text: t, tone: 'violet', wsf: null };
  // amber — waiting / stalled / stuck-with-owner / stale-run
  if (/\bwaiting\b/.test(t)) return { text: t, tone: 'amber', wsf: 'stalled' };
  const stuck = t.match(/stuck w\/ ([\w-]+)/);
  if (stuck) return { text: t, tone: 'amber', wsf: `assignee:${stuck[1]}` };
  if (/no run in/.test(t)) return { text: t, tone: 'amber', wsf: null };
  // quiet — "on track" and anything unrecognized (never fabricate urgency)
  return { text: t, tone: 'quiet', wsf: null };
}

/** Split the server's `attention` string (fragments joined by " · ") and
 *  classify each. "on track" (the server's non-attention sentinel) yields a
 *  single quiet fragment. */
export function parseAttention(attention: string): AttentionFragment[] {
  return (attention || '')
    .split(' · ')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(classifyFragment);
}
